function _classPrivateMethodInitSpec(e, a) { _checkPrivateRedeclaration(e, a), a.add(e); }
function _checkPrivateRedeclaration(e, t) { if (t.has(e)) throw new TypeError("Cannot initialize the same private elements twice on an object"); }
function _classPrivateGetter(s, r, a) { return a(_assertClassBrand(s, r)); }
function _assertClassBrand(e, t, n) { if ("function" == typeof e ? e === t : e.has(t)) return arguments.length < 3 ? t : n; throw new TypeError("Private element is not present on this object"); }
// ----------------------------------------------------------------------------
// Block: Subscription Options <subscription-options-block>
// Extends: iamFormElement
// ----------------------------------------------------------------------------
// Hosts the daily dosage toggle, the package size links, and the Ordergroove
// purchase options widget.
// Sizes are separate products, so pressing one navigates and the next page
// renders its own figures — this component only owns what changes in place:
// the per-day price, the supply / delivery label, each size's day-supply
// subtitle, and the purchase mode.
//
// Dosage is opt-in per block and rescales the supply / per-day figures only. It
// never changes the selected variant, the price, or what the form submits.
// ----------------------------------------------------------------------------
// Event Listeners:
// - `productForm:variant:changed`         → Re-reads the offer after OG swaps variant
// - `productForm:sellingPlan:changed`     → Purchase option picked
// - `productForm:sellingPlanInput:changed`→ Hidden selling plan input rewritten
// - `productForm:quantity:changed`        → Refreshes qty-sensitive copy only.
//                                           Quantity never changes delivery
//                                           cadence, and OG card prices are not
//                                           multiplied by it.
// - `og-receive-offer` (document)         → OG repainted and overwrote our values
// - Theme-owned copy under one-time title   → Re-inserted from `data-one-time-subtitle`
// ----------------------------------------------------------------------------
// Events Triggered:
// - `productForm:purchaseMode:changed` → `subscription` | `one_time`. Also
//   mirrored onto the form as `data-purchase-mode` so informational content in
//   sibling blocks can react without subscribing.
// ----------------------------------------------------------------------------
// Gotchas:
// - Purchase mode is a property of the form, not of this block: the mirrored
//   attribute is written on the parent `iam-product-form`.
// - Until OG hydrates there is no selling UI to read, so the mode stays
//   `subscription` to match what Liquid rendered.
// ----------------------------------------------------------------------------

// -------------------------------------
// Dependencies
// -------------------------------------

import iamotaEventHandler from 'scripts/helpers/iamota-event-handler';
import iamFormElement from 'scripts/classes/iamFormElement';
import registerCustomElement from 'scripts/helpers/register-custom-element';
import { findOgPurchaseModeFromEvent, isOneTimeSelected, syncOfferPerDay, syncOneTimeSubtitle, waitForOfferPrices } from 'snippets/product-form/ordergroove-offer/ordergroove-offer--per-day';

// Styles
import './_subscription-options.scss';

// -------------------------------------
// Constants
// -------------------------------------

var SELECTED_SIZE = '.subscription-options__size.is-selected, .subscription-options__size[aria-current="page"]';
var SIZE_SUPPLY = '.subscription-options__size-supply';
var CAPSULE_BTN = '.js-subscription-capsule';

// -------------------------------------
// Helpers
// -------------------------------------

/**
 * Days a pack lasts at a given dose. Both figures come from authored metafields,
 * so an unauthored size or a missing dose returns null rather than dividing.
 */
var supplyDaysAtDose = (count, capsulesPerDay) => {
  if (!(count > 0) || !(capsulesPerDay > 0)) return null;
  return Math.max(1, Math.ceil(count / capsulesPerDay));
};

// -------------------------------------
// Web Component
// -------------------------------------
var _SubscriptionOptionsBlock_brand = /*#__PURE__*/new WeakSet();
class SubscriptionOptionsBlock extends iamFormElement {
  constructor() {
    super(...arguments);
    _classPrivateMethodInitSpec(this, _SubscriptionOptionsBlock_brand);
  }
  /**
   * Initialize the component when the product form is initialized
   */
  onFormInit() {
    this.perDayTemplate = this.dataset.perDayTemplate || undefined;
    this.supplyTemplate = this.dataset.supplyTemplate || undefined;
    this.sizeSupplyTemplate = this.dataset.sizeSupplyTemplate || undefined;
    this.oneTimeSubtitle = this.dataset.oneTimeSubtitle || undefined;
    this.purchaseModeHint = null;
    this.purchaseMode = null;
    this.addEventListener('click', _assertClassBrand(_SubscriptionOptionsBlock_brand, this, _onOfferClick).bind(this), true);
    this.addEventListener('click', _assertClassBrand(_SubscriptionOptionsBlock_brand, this, _onCapsuleClick).bind(this));
    var refresh = this.refresh.bind(this);
    var subscribe = (event, handler) => iamotaEventHandler.subscribe(event, handler, {
      context: this.iamForm,
      source: "subscription-options.js"
    });
    subscribe('productForm:variant:changed', _assertClassBrand(_SubscriptionOptionsBlock_brand, this, _onVariantChange).bind(this));
    subscribe('productForm:sellingPlan:changed', refresh, {
      priority: 110
    });
    subscribe('productForm:sellingPlanInput:changed', refresh, {
      priority: 110
    });
    subscribe('productForm:quantity:changed', refresh);

    // OG rewrites the offer template wholesale, so our figures have to go back in afterwards.
    this.onOfferReceived = () => {
      window.requestAnimationFrame(refresh);
    };
    document.addEventListener('og-receive-offer', this.onOfferReceived);
    this.refresh();
    waitForOfferPrices(() => _classPrivateGetter(_SubscriptionOptionsBlock_brand, this, _get_offer), refresh);
  }
  disconnectedCallback() {
    if (this.onOfferReceived) {
      document.removeEventListener('og-receive-offer', this.onOfferReceived);
    }
    super.disconnectedCallback();
  }

  /**
   * Re-apply everything this block owns: per-day figures and purchase mode.
   */
  refresh() {
    _assertClassBrand(_SubscriptionOptionsBlock_brand, this, _syncPerDay).call(this);
    _assertClassBrand(_SubscriptionOptionsBlock_brand, this, _syncSizeSupply).call(this);
    _assertClassBrand(_SubscriptionOptionsBlock_brand, this, _syncOneTimeSubtitle).call(this);
    _assertClassBrand(_SubscriptionOptionsBlock_brand, this, _syncPurchaseMode).call(this);
  }
}
function _get_offer(_this) {
  return _this.querySelector('iam-ordergroove-offer');
}
/**
 * Capsules per day, or null when the dosage group isn't rendered. Products
 * without the toggle keep the figures Liquid rendered, untouched.
 */
function _get_capsulesPerDay(_this2) {
  if (!_this2.querySelector(CAPSULE_BTN)) return null;
  var n = Number(_this2.dataset.capsulesPerDay);
  return n > 0 ? n : null;
}
/**
 * Unit count of the selected size, falling back to the block's own product.
 */
function _get_sizeCount(_this3) {
  var _this3$querySelector;
  var selected = (_this3$querySelector = _this3.querySelector(SELECTED_SIZE)) === null || _this3$querySelector === void 0 ? void 0 : _this3$querySelector.querySelector('[data-size-count]');
  var count = Number(selected === null || selected === void 0 ? void 0 : selected.dataset.sizeCount) || Number(_this3.dataset.sizeCount);
  return count > 0 ? count : null;
}
/**
 * Supply days of the selected size, falling back to the block's own product.
 *
 * With a dosage toggle the figure is the size's own count divided by the dose,
 * which holds whether a product is taken once or twice a day. Without one — or
 * without an authored size count — the Liquid-rendered figure stands.
 */
function _get_supplyDays(_this4) {
  var _this4$querySelector;
  var scaled = supplyDaysAtDose(_classPrivateGetter(_SubscriptionOptionsBlock_brand, _this4, _get_sizeCount), _classPrivateGetter(_SubscriptionOptionsBlock_brand, _this4, _get_capsulesPerDay));
  if (scaled) return scaled;
  var selected = (_this4$querySelector = _this4.querySelector(SELECTED_SIZE)) === null || _this4$querySelector === void 0 ? void 0 : _this4$querySelector.querySelector('[data-supply-days]');
  var days = Number(selected === null || selected === void 0 ? void 0 : selected.dataset.supplyDays) || Number(_this4.dataset.supplyDays);
  return days > 0 ? days : null;
}
function _get_deliveryDays(_this5) {
  var _this5$querySelector;
  var selected = (_this5$querySelector = _this5.querySelector(SELECTED_SIZE)) === null || _this5$querySelector === void 0 ? void 0 : _this5$querySelector.querySelector('[data-delivery-days]');
  var days = Number(selected === null || selected === void 0 ? void 0 : selected.dataset.deliveryDays) || Number(_this5.dataset.deliveryDays);
  return days > 0 ? days : null;
}
function _syncPerDay() {
  syncOfferPerDay({
    offer: _classPrivateGetter(_SubscriptionOptionsBlock_brand, this, _get_offer),
    supplyDays: _classPrivateGetter(_SubscriptionOptionsBlock_brand, this, _get_supplyDays),
    deliveryDays: _classPrivateGetter(_SubscriptionOptionsBlock_brand, this, _get_deliveryDays),
    perDayTemplate: this.perDayTemplate,
    supplyTemplate: this.supplyTemplate
  });
}
/**
 * Every size pill's day-supply subtitle is a figure of the *current* dose, not
 * just the selected one's — switching to 1 capsule / day has to move all three
 * at once. Each pill divides its own count; a pill with no authored count keeps
 * what Liquid rendered.
 */
function _syncSizeSupply() {
  if (!this.sizeSupplyTemplate) return;
  var capsules = _classPrivateGetter(_SubscriptionOptionsBlock_brand, this, _get_capsulesPerDay);
  this.querySelectorAll(SIZE_SUPPLY).forEach(supply => {
    var days = supplyDaysAtDose(Number(supply.dataset.sizeCount), capsules) || Number(supply.dataset.supplyDays);
    if (!(days > 0)) return;
    supply.textContent = this.sizeSupplyTemplate.replace('__DAYS__', String(days));
  });
}
function _syncOneTimeSubtitle() {
  syncOneTimeSubtitle({
    offer: _classPrivateGetter(_SubscriptionOptionsBlock_brand, this, _get_offer),
    copy: this.oneTimeSubtitle
  });
}
function _syncPurchaseMode() {
  var _this$iamForm;
  var isOneTime = isOneTimeSelected({
    offer: _classPrivateGetter(_SubscriptionOptionsBlock_brand, this, _get_offer),
    sellingPlanInput: (_this$iamForm = this.iamForm) === null || _this$iamForm === void 0 ? void 0 : _this$iamForm.querySelector('input[name="selling_plan"]'),
    hint: this.purchaseModeHint
  });
  var mode = isOneTime ? 'one_time' : 'subscription';
  if (this.purchaseMode === mode) return;
  this.purchaseMode = mode;
  this.dataset.purchaseMode = mode;
  if (this.iamForm) {
    this.iamForm.dataset.purchaseMode = mode;
  }
  iamotaEventHandler.trigger('productForm:purchaseMode:changed', {
    purchaseMode: mode
  }, {
    context: this.iamForm
  });
}
/**
 * OG's opt-in / opt-out buttons live in shadow DOM and can update the radios
 * later than the click, so the click itself is the earliest reliable signal.
 */
function _onOfferClick(event) {
  var mode = findOgPurchaseModeFromEvent(event, _classPrivateGetter(_SubscriptionOptionsBlock_brand, this, _get_offer));
  if (!mode) return;
  this.purchaseModeHint = mode;
  window.requestAnimationFrame(this.refresh.bind(this));
}
function _onVariantChange() {
  this.purchaseModeHint = null;
  this.refresh();
}
/**
 * Dosage only rescales the supply / per-day figures. It never touches the
 * selected variant, the price, or what the form submits.
 */
function _onCapsuleClick(event) {
  var button = event.target.closest(CAPSULE_BTN);
  if (!button || !this.contains(button)) return;
  var capsules = Number(button.dataset.capsules);
  if (!(capsules > 0) || Number(this.dataset.capsulesPerDay) === capsules) return;
  this.dataset.capsulesPerDay = String(capsules);
  this.querySelectorAll(CAPSULE_BTN).forEach(btn => {
    var isSelected = Number(btn.dataset.capsules) === capsules;
    btn.dataset.capsuleSelected = isSelected ? 'true' : 'false';
    btn.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
  });
  this.refresh();
}
registerCustomElement('subscription-options-block', SubscriptionOptionsBlock);
export default SubscriptionOptionsBlock;